import axiosInstance from '../axiosConfig';
import {
  Court,
  CourtReservation,
  CourtReservationFormData,
  CourtOperatingHours,
  CourtSchedule,
  CourtAvailability,
  CourtsResponse,
  CourtResponse,
  CourtReservationResponse,
  CourtReservationsResponse,
  CourtScheduleResponse,
  CourtAvailabilityResponse,
  CourtOperatingHoursResponse,
} from '../../types';

/**
 * Court Service
 * Handles all Court-related API calls
 */
class CourtService {
  private baseUrl = '/courts';

  // ==========================================
  // Court Endpoints
  // ==========================================

  /**
   * Get all courts
   * @param type - Optional filter by court type (tennis/pickleball)
   * @param includeInactive - Include inactive courts
   * @returns List of courts
   */
  async getAllCourts(type?: string, includeInactive: boolean = false): Promise<Court[]> {
    const params: any = {};
    if (type) params.type = type;
    if (includeInactive) params.include_inactive = 'true';

    const response = await axiosInstance.get<CourtsResponse>(`${this.baseUrl}/`, { params });
    return response.data.data;
  }

  /**
   * Get court by ID
   * @param courtId - Court ID
   * @returns Court details
   */
  async getCourtById(courtId: string): Promise<Court> {
    const response = await axiosInstance.get<CourtResponse>(`${this.baseUrl}/${courtId}`);
    return response.data.data;
  }

  /**
   * Get available courts
   * @param type - Optional filter by court type
   * @returns List of available courts
   */
  async getAvailableCourts(type?: string): Promise<Court[]> {
    const params: any = {};
    if (type) params.type = type;

    const response = await axiosInstance.get<CourtsResponse>(`${this.baseUrl}/available`, { params });
    return response.data.data;
  }

  /**
   * Update court status (Admin only)
   * @param courtId - Court ID
   * @param status - New status (available/maintenance/closed)
   * @returns Updated court
   */
  async updateCourtStatus(courtId: string, status: string): Promise<Court> {
    const response = await axiosInstance.put<CourtResponse>(
      `${this.baseUrl}/${courtId}/status`,
      { status }
    );
    return response.data.data;
  }

  // ==========================================
  // Operating Hours Endpoints
  // ==========================================

  /**
   * Get operating hours
   * @param courtId - Optional court ID for court-specific hours
   * @returns Operating hours
   */
  async getOperatingHours(courtId?: string): Promise<CourtOperatingHours[]> {
    const params: any = {};
    if (courtId) params.court_id = courtId;

    const response = await axiosInstance.get<CourtOperatingHoursResponse>(
      `${this.baseUrl}/hours`,
      { params }
    );
    return response.data.data;
  }

  /**
   * Update operating hours (Admin only)
   * @param hoursId - Operating hours ID
   * @param data - Updated hours data
   * @returns Updated operating hours
   */
  async updateOperatingHours(
    hoursId: string,
    data: { open_time?: string; close_time?: string; is_closed?: boolean }
  ): Promise<CourtOperatingHours> {
    const response = await axiosInstance.put<{ success: boolean; data: CourtOperatingHours }>(
      `${this.baseUrl}/hours/${hoursId}`,
      data
    );
    return response.data.data;
  }

  // ==========================================
  // Schedule Endpoints
  // ==========================================

  /**
   * Get court schedule for a specific date
   * @param courtId - Court ID
   * @param date - Date string (YYYY-MM-DD)
   * @returns Court schedule with reservations
   */
  async getCourtSchedule(courtId: string, date: string): Promise<CourtSchedule> {
    const response = await axiosInstance.get<CourtScheduleResponse>(
      `${this.baseUrl}/${courtId}/schedule`,
      { params: { date } }
    );
    return response.data.data;
  }

  /**
   * Check availability for a time slot
   * @param courtId - Court ID
   * @param startTime - Start time ISO string
   * @param duration - Duration in minutes (30, 60, or 90)
   * @returns Availability status
   */
  async checkAvailability(
    courtId: string,
    startTime: string,
    duration: number
  ): Promise<CourtAvailability> {
    const response = await axiosInstance.get<CourtAvailabilityResponse>(
      `${this.baseUrl}/availability`,
      {
        params: {
          court_id: courtId,
          start_time: startTime,
          duration: duration,
        },
      }
    );
    return response.data.data;
  }

  // ==========================================
  // Reservation Endpoints
  // ==========================================

  /**
   * Create a new reservation
   * @param data - Reservation form data
   * @returns Created reservation
   */
  async createReservation(data: CourtReservationFormData): Promise<CourtReservation> {
    const response = await axiosInstance.post<CourtReservationResponse>(
      `${this.baseUrl}/reservations`,
      data
    );
    return response.data.data;
  }

  /**
   * Get reservation by ID
   * @param reservationId - Reservation ID
   * @returns Reservation details
   */
  async getReservationById(reservationId: string): Promise<CourtReservation> {
    const response = await axiosInstance.get<CourtReservationResponse>(
      `${this.baseUrl}/reservations/${reservationId}`
    );
    return response.data.data;
  }

  /**
   * Get current user's reservations
   * @param includePast - Include past reservations
   * @returns List of reservations
   */
  async getMyReservations(includePast: boolean = false): Promise<CourtReservation[]> {
    const response = await axiosInstance.get<CourtReservationsResponse>(
      `${this.baseUrl}/reservations/my`,
      { params: { include_past: includePast ? 'true' : 'false' } }
    );
    return response.data.data;
  }

  /**
   * Get reservations for a member
   * @param memberId - Member ID
   * @param includePast - Include past reservations
   * @returns List of reservations
   */
  async getMemberReservations(memberId: string, includePast: boolean = false): Promise<CourtReservation[]> {
    const response = await axiosInstance.get<CourtReservationsResponse>(
      `${this.baseUrl}/reservations/member/${memberId}`,
      { params: { include_past: includePast ? 'true' : 'false' } }
    );
    return response.data.data;
  }

  /**
   * Cancel a reservation
   * @param reservationId - Reservation ID
   * @param reason - Optional cancellation reason
   * @returns Cancelled reservation
   */
  async cancelReservation(reservationId: string, reason?: string): Promise<CourtReservation> {
    const response = await axiosInstance.delete<CourtReservationResponse>(
      `${this.baseUrl}/reservations/${reservationId}`,
      { data: { reason: reason || null } }
    );
    return response.data.data;
  }

  /**
   * Get reservations for a date range (Staff only)
   * @param startDate - Start date (YYYY-MM-DD)
   * @param endDate - End date (YYYY-MM-DD)
   * @param courtId - Optional court filter
   * @returns List of reservations
   */
  async getReservationsForDateRange(
    startDate: string,
    endDate: string,
    courtId?: string
  ): Promise<CourtReservation[]> {
    const params: any = { start_date: startDate, end_date: endDate };
    if (courtId) params.court_id = courtId;

    const response = await axiosInstance.get<CourtReservationsResponse>(
      `${this.baseUrl}/reservations`,
      { params }
    );
    return response.data.data;
  }

  // ==========================================
  // Helper Methods
  // ==========================================

  /**
   * Generate time slots for a given date and operating hours
   * @param date - Date string (YYYY-MM-DD)
   * @param operatingHours - Operating hours for the day
   * @param duration - Slot duration in minutes
   * @returns Array of time slot strings
   */
  generateTimeSlots(
    date: string,
    operatingHours: CourtOperatingHours,
    duration: number = 60
  ): string[] {
    const slots: string[] = [];
    if (operatingHours.is_closed) return slots;

    const [openHour, openMin] = operatingHours.open_time.split(':').map(Number);
    const [closeHour, closeMin] = operatingHours.close_time.split(':').map(Number);

    const openMinutes = openHour * 60 + openMin;
    const closeMinutes = closeHour * 60 + closeMin;

    for (let mins = openMinutes; mins + duration <= closeMinutes; mins += 30) {
      const hour = Math.floor(mins / 60);
      const min = mins % 60;
      const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      slots.push(`${date}T${timeStr}:00`);
    }

    return slots;
  }

  /**
   * Format time for display
   * @param isoString - ISO date string
   * @returns Formatted time string (e.g., "10:30 AM")
   */
  formatTime(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  /**
   * Format date for display
   * @param isoString - ISO date string
   * @returns Formatted date string (e.g., "Mon, Jan 15")
   */
  formatDate(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * Get duration label
   * @param minutes - Duration in minutes
   * @returns Human-readable duration
   */
  getDurationLabel(minutes: number): string {
    if (minutes === 30) return '30 min';
    if (minutes === 60) return '1 hour';
    if (minutes === 90) return '1.5 hours';
    return `${minutes} min`;
  }
}

// Export singleton instance
const courtService = new CourtService();
export default courtService;
